"use client";
import React, { useCallback } from 'react';
import { Card, CardVariant, isVariantOutOfStock } from '../../types/card';
import { useTranslations } from 'next-intl';
import { SITE_CONFIG } from '@/constants';
import { theme as customTheme } from '@/src/theme';
import { countryFlagSrc, getCountryName } from '../../helpers/countries';
import { variantAmountLabel } from './productUtils';
import { LocalOfferIcon } from '../icons';
import pdStyles from './StyledComponents.module.css';
import styles from './StyledComponents.module.css';

interface VariantSelectorProps {
  product: Card;
  selectedVariantIndex: number;
  customValue: number | null;
  validationMsg: string | null;
  outOfStock: boolean;
  onVariantSelect: (index: number) => void;
  onCustomValueChange: (value: number | null, variant: CardVariant) => void;
  onCustomValueBlur: (value: string, variant: CardVariant) => void;
}

export const VariantSelector = React.memo<VariantSelectorProps>(({
  product,
  selectedVariantIndex,
  customValue,
  validationMsg,
  outOfStock,
  onVariantSelect,
  onCustomValueChange,
  onCustomValueBlur,
}) => {
  const t = useTranslations('product');
  const selectedVariant = product.variants?.[selectedVariantIndex];

  const renderPricingTitle = () => {
    if (!product.variants?.length) return t('pricing.enterAmount');
    if (product.variants.length > 1) return t('pricing.selectAmount');
    if (product.variants[0]?.faceValue.min === product.variants[0]?.faceValue.max) return null;
    return t('pricing.enterAmount');
  };

  const title = renderPricingTitle();

  const isMultiVariant = product.variants && product.variants.length > 1 && selectedVariant;
  const isSingleRange = product.variants && product.variants.length === 1 && selectedVariant && selectedVariant.faceValue.min !== selectedVariant.faceValue.max;
  const showRangeInput = selectedVariant && selectedVariant.faceValue.min !== selectedVariant.faceValue.max;

  return (
    <>
      <h3 className={styles.sectionTitle}>
        <LocalOfferIcon className={styles.sectionIcon} />
        {title}
      </h3>

      {isMultiVariant && (
        <div className={styles.selectWrapper}>
          <VariantTiles
            variants={product.variants!}
            selectedVariantIndex={selectedVariantIndex}
            discount={product.discountPct}
            outOfStock={outOfStock}
            onSelect={onVariantSelect}
          />
          {showRangeInput && (
            <RangeInput
              variant={selectedVariant}
              customValue={customValue}
              validationMsg={validationMsg}
              outOfStock={outOfStock}
              onValueChange={onCustomValueChange}
              onValueBlur={onCustomValueBlur}
            />
          )}
        </div>
      )}

      {isSingleRange && (
        <div className={styles.selectWrapper}>
          <VariantTiles
            variants={product.variants!}
            selectedVariantIndex={selectedVariantIndex}
            discount={product.discountPct}
            outOfStock={outOfStock}
            onSelect={onVariantSelect}
          />
          <RangeInput
            variant={selectedVariant}
            customValue={customValue}
            validationMsg={validationMsg}
            outOfStock={outOfStock}
            onValueChange={onCustomValueChange}
            onValueBlur={onCustomValueBlur}
          />
        </div>
      )}
    </>
  );
});

VariantSelector.displayName = 'VariantSelector';

interface VariantTilesProps {
  variants: CardVariant[];
  selectedVariantIndex: number;
  discount: number | undefined;
  outOfStock: boolean;
  onSelect: (index: number) => void;
}

const VariantTiles = React.memo<VariantTilesProps>(({
  variants,
  selectedVariantIndex,
  // discount and outOfStock are kept on the props for the (currently
  // commented-out) per-tile discount ribbon; the active layout doesn't use them.
  onSelect,
}) => {
  return (
    <div className={styles.priceTileGrid}>
      {variants.map((variant, index) => {
        const isRange = variant.faceValue.min !== variant.faceValue.max;
        const isSelected = index === selectedVariantIndex;
        const variantOos = isVariantOutOfStock(variant);
        const countries = variant.countries ?? [];
        const primaryCountry = countries[0]?.toUpperCase();
        const extraCount = Math.max(0, countries.length - 1);
        const isWorldwide = primaryCountry === 'WW';
        const headline = primaryCountry ? getCountryName(primaryCountry) : variant.currency;
        const flagSrc = primaryCountry && !isWorldwide
          ? countryFlagSrc(primaryCountry)
          : null;
        const subtitle = variantAmountLabel(variant);
        return (
          <div
            key={variant.sku}
            className={`${styles.priceTile}${isSelected ? ` ${styles.selected}` : ''}${variantOos ? ` ${styles.priceTileOos}` : ''}`}
            onClick={() => onSelect(index)}
          >
            <div className={styles.priceTileHeader}>
              {flagSrc ? (
                <img
                  src={flagSrc}
                  alt=""
                  aria-hidden="true"
                  loading="lazy"
                  className={styles.priceTileFlag}
                />
              ) : (
                <span className={styles.priceTileFlag} aria-hidden="true">{isWorldwide ? '🌍' : '🌐'}</span>
              )}
              <div className={styles.priceTileLabels}>
                <div className={styles.priceTileCountry}>
                  <span className={styles.priceTileCountryName}>{headline}</span>
                  {extraCount > 0 && <span className={styles.priceTileExtra}>+{extraCount}</span>}
                </div>
                <div className={styles.priceTileSubtitle}>{subtitle}</div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
});

VariantTiles.displayName = 'VariantTiles';

interface RangeInputProps {
  variant: CardVariant;
  customValue: number | null;
  validationMsg: string | null;
  outOfStock: boolean;
  onValueChange: (value: number | null, variant: CardVariant) => void;
  onValueBlur: (value: string, variant: CardVariant) => void;
}

const RangeInput = React.memo<RangeInputProps>(({
  variant,
  customValue,
  validationMsg,
  outOfStock,
  onValueChange,
  onValueBlur,
}) => {
  const t = useTranslations('product');
  const rawStep = variant.faceValue.step;
  const step = rawStep ? Math.max(SITE_CONFIG.product.minDenominationStep, rawStep) : undefined;
  const currentValue = customValue ?? variant.faceValue.max;

  const snapToStep = useCallback((value: number): number => {
    if (!step) return value;
    const snapped = Math.round(value / step) * step;
    return Math.round(Math.min(variant.faceValue.max, Math.max(variant.faceValue.min, snapped)) * 100) / 100;
  }, [step, variant.faceValue.min, variant.faceValue.max]);

  const handleStep = useCallback((direction: 1 | -1) => {
    if (!step) return;
    const snapped = snapToStep(currentValue);
    const next = snapped + direction * step;
    const clamped = Math.min(variant.faceValue.max, Math.max(variant.faceValue.min, next));
    const rounded = Math.round(clamped * 100) / 100;
    onValueChange(rounded, variant);
  }, [step, currentValue, snapToStep, variant, onValueChange]);

  return (
    <>
      <div className={pdStyles.rangeInputBox}>
        <div className={`${styles.priceInputContainer}${validationMsg ? ` ${styles.hasError}` : ''}`}>
          <div className={outOfStock ? pdStyles.opacityWrapperDisabled : pdStyles.opacityWrapper}>
            <div className={styles.inputFlexContainer}>
              {step && (
                <button
                  className={styles.stepperButton}
                  onClick={() => handleStep(-1)}
                  disabled={outOfStock || currentValue <= variant.faceValue.min}
                  aria-label="Decrease value"
                >−</button>
              )}
              <input
                className={`${styles.priceInput}${validationMsg ? ` ${styles.hasError}` : ''}`}
                type="number"
                min={variant.faceValue.min}
                max={variant.faceValue.max}
                step={step || 'any'}
                value={customValue ?? ''} /* let the user fill desired value */
                disabled={outOfStock}
                onChange={(e) => {
                  const v = parseFloat(e.target.value);
                  if (!isNaN(v)) onValueChange(v, variant);
                  else if (e.target.value === '') { onValueChange(null, variant); }
                }}
                onBlur={(e) => onValueBlur(e.target.value, variant)}
              />
              <span className={styles.currencyDisplay}>{variant.currency}</span>
              {step && (
                <button
                  className={styles.stepperButton}
                  onClick={() => handleStep(1)}
                  disabled={outOfStock || currentValue >= variant.faceValue.max}
                  aria-label="Increase value"
                >+</button>
              )}
            </div>
          </div>
        </div>
      </div>

      {validationMsg && (
        <div
          className={`${styles.helperText}${!validationMsg.includes('Adjusted') ? ` ${styles.error}` : ''}`}
          style={{ color: validationMsg.includes('Adjusted') ? customTheme.colors.success : customTheme.colors.secondary }}
        >
          {validationMsg}
        </div>
      )}

      <div className={styles.rangeText}>
        {t('pricing.allowedRange')}: {variant.currency} {Math.round(variant.faceValue.min)} - {Math.round(variant.faceValue.max)}
        {step ? ` (${t('pricing.step')}: ${step})` : ''}
      </div>
    </>
  );
});

RangeInput.displayName = 'RangeInput';
